import { useEffect, useRef, useState } from 'react';

import { StorageService } from '~/services/StorageService/StorageService';
import { logger } from '~/utils/logger';

type Fn<T> = () => Promise<T>;

interface Options {
  skip?: boolean;
  onError?: (error: unknown) => void;
}

export const useStaleWhileRevalidate = <T>(
  key: string,
  fetchData: Fn<T>,
  { skip, onError }: Options = {},
) => {
  const [data, setData] = useState<T | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  // need this to be a ref since we cannot update the ref inside of promise.then
  const isCachedValueRef = useRef(false);
  const fetchDataRef = useRef(fetchData);
  const onErrorRef = useRef(onError);

  useEffect(() => {
    fetchDataRef.current = fetchData;
    onErrorRef.current = onError;
  });

  useEffect(() => {
    isCachedValueRef.current = false;
    setData(null);
  }, [key]);

  useEffect(() => {
    if (skip) return;

    StorageService.isReady().then(async () => {
      try {
        const stored = await StorageService.getSecureItem<T>(key);
        if (!isCachedValueRef.current && stored !== null) {
          setData(stored);
          isCachedValueRef.current = true;
        }
      } catch (error) {
        logger.hooks('Error loading from storage for key %s: %o', key, error);
      }
    });
  }, [key, skip]);

  useEffect(() => {
    if (skip) return;

    setIsLoading(true);

    (async () => {
      let freshData: T;
      try {
        freshData = await fetchDataRef.current();
        isCachedValueRef.current = true;
        setData(freshData);
      } catch (error) {
        logger.hooks('Error fetching fresh data for key %s: %o', key, error);
        onErrorRef.current?.(error);
        return;
      } finally {
        setIsLoading(false);
      }

      try {
        await StorageService.setSecureItem(key, freshData);
      } catch (error) {
        logger.hooks('Error saving fresh data for key %s: %o', key, error);
      }
    })();
  }, [key, skip]);

  return { data, isLoading, isCachedValue: isCachedValueRef.current };
};
